import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import '../Css/Home.css';
import Header from '../Components/Header';
import LanguageSwitcher from '../Components/LanguageSwitcher';
import {
  FaPalette, FaGlobe,
  FaRocket, FaBullseye, FaLightbulb, FaChartLine, FaCheck, FaSync,
  FaHandshake, FaBuilding, FaPhone, FaEnvelope, FaWhatsapp, FaCircle,
  FaCrown, FaExternalLinkAlt, FaLinkedin, FaFacebook, FaArrowRight,
  FaClock, FaCheckCircle, FaChartPie, FaBolt, FaPaperPlane, FaTwitter,
  FaPinterest, FaCode, FaMagic, FaInfinity
} from 'react-icons/fa';

interface ContactInfo {
  whatsapp: string[];
  phoneNumbers: string[];
  emails: string[];
}

const contactInfo: ContactInfo = {
  whatsapp: ['+243978089552'],
  phoneNumbers: ['+250729606087', '+243978089552'],
  emails: ['info@quevvy.com', 'quevvy.platform@gmail.com'],
};

const homeNavItems = [
  { id: 'home', labelKey: 'nav.home' },
  { id: 'project', labelKey: 'nav.project' },
  { id: 'features', labelKey: 'nav.features' },
  { id: 'roadmap', labelKey: 'nav.roadmap' },
  { id: 'investment', labelKey: 'nav.investment' },
  { id: 'contact', labelKey: 'nav.contact' },
];

const featureKeys = ['builder', 'generation', 'free', 'design', 'vibecoding', 'multilingual'] as const;
const featureIcons: Record<typeof featureKeys[number], React.ReactNode> = {
  builder: <FaCode />,
  generation: <FaRocket />,
  free: <FaInfinity />,
  design: <FaPalette />,
  vibecoding: <FaMagic />,
  multilingual: <FaGlobe />,
};
const featureColors = ['cyan', 'violet', 'emerald', 'amber', 'rose', 'violet'] as const;

const roadmapKeys = ['dev', 'tests', 'payments', 'partners', 'expansion'] as const;
const roadmapStatus: Array<'completed' | 'current' | 'future'> = ['completed', 'completed', 'current', 'future', 'future'];

const galleryItems = [
  { id: 1, key: 'portfolio', imageUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop' },
  { id: 2, key: 'landing', imageUrl: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=400&h=300&fit=crop' },
  { id: 3, key: 'blog', imageUrl: 'https://images.unsplash.com/photo-1499750310107-5fef28fd666f?w=400&h=300&fit=crop' },
  { id: 4, key: 'ecommerce', imageUrl: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop' },
  { id: 5, key: 'startup', imageUrl: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=400&h=300&fit=crop' },
  { id: 6, key: 'creative', imageUrl: 'https://images.unsplash.com/photo-1507233506556-97a1d8f8e0c5?w=400&h=300&fit=crop' },
];

const statusItemCount = 6;

const Home: React.FC = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<string>('home');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    interest: 'investor'
  });

  const heroRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const roadmapRef = useRef<HTMLDivElement>(null);
  const investmentRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.className = 'dark-theme';
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'project', 'features', 'roadmap', 'investment', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getInterestLabel = (interest: string): string => t(`contact.interests.${interest}`);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const message = `🌟 ${t('whatsapp.partnershipTitle')} 🌟

👤 ${t('whatsapp.name')}: ${formData.name}
📧 ${t('whatsapp.email')}: ${formData.email}
📱 ${t('whatsapp.phone')}: ${formData.phone || t('whatsapp.phoneNotProvided')}
🎯 ${t('whatsapp.interest')}: ${getInterestLabel(formData.interest)}

💬 ${t('whatsapp.message')}:
${formData.message}`;

    const whatsappNumber = contactInfo.whatsapp[0].replace('+', '');
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');

    setFormData({ name: '', email: '', phone: '', message: '', interest: 'investor' });
  };

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      window.scrollTo({ top: element.offsetTop - 80, behavior: 'smooth' });
    }
  };

  return (
    <div className="quevvy-container">
      <Header
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        scrollToSection={scrollToSection}
        navItems={homeNavItems}
      />

      {/* HERO */}
      <section id="home" className="hero-section" ref={heroRef}>
        <div className="hero-top-bar">
          <LanguageSwitcher />
        </div>

        <div className="hero-bg">
          <div className="hero-mesh"></div>
          <div className="hero-grid"></div>
          <div className="hero-glow hero-glow-1"></div>
          <div className="hero-glow hero-glow-2"></div>
        </div>

        <div className="hero-content">
          <div className="hero-badge">
            <FaBolt className="hero-badge-icon" />
            <span>{t('hero.badge')}</span>
          </div>

          <div className="hero-logo">
            <img src="/img/quevvy_logo_transparent.png" alt="Quevvy" />
          </div>

          <h1 className="hero-title">
            {t('hero.title')} <span className="highlight">{t('hero.titleHighlight')}</span>
          </h1>

          <p className="hero-subtitle">{t('hero.subtitle')}</p>

          <div className="hero-stats">
            {(['free', 'unlimited', 'speed', 'nocode'] as const).map((key) => (
              <div key={key} className="hero-stat">
                <div className="hero-stat-number">{t(`hero.stats.${key}.value`)}</div>
                <div className="hero-stat-label">{t(`hero.stats.${key}.label`)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="hero-scroll" onClick={() => scrollToSection('project')}>
          <span>{t('hero.cta')}</span>
          <div className="hero-scroll-line"></div>
        </div>
      </section>

      {/* PROJECT */}
      <section id="project" className="section project-section">
        <div className="section-container">
          <div className="section-header">
            <span className="section-badge">{t('project.badge')}</span>
            <h2 className="section-title">
              {t('project.title')} <span className="highlight">{t('project.titleHighlight')}</span>
            </h2>
            <p className="section-subtitle">{t('project.subtitle')}</p>
          </div>

          <div className="project-grid">
            <div className="project-card">
              <h3 className="project-card-title">
                <span className="icon"><FaCode /></span>
                {t('project.whatIs')}
              </h3>
              <p>{t('project.description')}</p>

              <div className="project-highlights">
                {(['platform', 'audience', 'innovation', 'market'] as const).map((key) => (
                  <div key={key} className="highlight-item">
                    <div className={`highlight-icon ${key === 'platform' ? 'cyan' : key === 'audience' ? 'violet' : key === 'innovation' ? 'amber' : 'emerald'}`}>
                      {key === 'platform' ? <FaGlobe /> : key === 'audience' ? <FaBullseye /> : key === 'innovation' ? <FaLightbulb /> : <FaChartLine />}
                    </div>
                    <div className="highlight-content">
                      <h4>{t(`project.highlights.${key}.title`)}</h4>
                      <p>{t(`project.highlights.${key}.desc`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="status-card">
              <div className="status-header">
                <span className="status-badge completed"><FaCheck /></span>
                <h4>{t('project.status.title')}</h4>
              </div>

              <ul className="status-list">
                {Array.from({ length: statusItemCount }, (_, i) => (
                  <li key={i}><FaCheck /> {t(`project.status.items.${i}`)}</li>
                ))}
              </ul>

              <div className="product-status-block waiting">
                <div className="product-status-header">
                  <span className="status-badge in-progress"><FaSync /></span>
                  <h4>{t('project.status.waitingProduct.title')}</h4>
                </div>
                <p>{t('project.status.waitingProduct.desc')}</p>
              </div>

              <div className="product-status-block future">
                <div className="product-status-header">
                  <span className="status-badge future"><FaClock /></span>
                  <div>
                    <h4>{t('project.status.futureProduct.title')}</h4>
                    <span className="future-badge">{t('project.status.futureProduct.badge')}</span>
                  </div>
                </div>
                <p>{t('project.status.futureProduct.desc')}</p>
                <a href="https://tickets.quevvy.com" target="_blank" rel="noopener noreferrer" className="future-link">
                  <FaExternalLinkAlt /> {t('project.status.futureProduct.link')}
                </a>
              </div>

              <div className="status-header" style={{ marginTop: '1.5rem' }}>
                <span className="status-badge in-progress"><FaSync /></span>
                <h4>{t('project.status.partnership.title')}</h4>
              </div>
              <p className="status-note">{t('project.status.partnership.desc')}</p>

              <div className="investment-box">
                <h4>{t('project.status.investment.title')}</h4>
                <div className="investment-amount">{t('project.status.investment.amount')}</div>
                <p className="investment-note">{t('project.status.investment.note')}</p>
              </div>
            </div>
          </div>

          <div className="project-media-showcase">
            <div className="project-partnership-image">
              <img src="/img/images/partnership-image.jpg" alt="Quevvy Partnership" />
            </div>
            <div className="project-logos">
              <h4 className="project-logos-title">{t('project.gallery.title')}</h4>
              <p className="project-logos-subtitle">{t('project.gallery.subtitle')}</p>
              <div className="project-logos-grid">
                {galleryItems.map(item => (
                  <div key={item.id} className="gallery-event-card">
                    <img src={item.imageUrl} alt={t(`gallery.${item.key}`)} className="gallery-event-image" />
                    <div className="gallery-event-overlay">
                      <span className="gallery-event-title">{t(`gallery.${item.key}`)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="section features-section" ref={featuresRef}>
        <div className="section-container">
          <div className="section-header">
            <span className="section-badge">{t('features.badge')}</span>
            <h2 className="section-title">
              {t('features.title')} <span className="highlight">{t('features.titleHighlight')}</span> ?
            </h2>
            <p className="section-subtitle">{t('features.subtitle')}</p>
          </div>

          <div className="features-grid">
            {featureKeys.map((key, index) => (
              <div key={key} className="feature-card">
                <div className={`feature-icon ${featureColors[index]}`}>
                  {featureIcons[key]}
                </div>
                <h3 className="feature-title">{t(`features.items.${key}.title`)}</h3>
                <p className="feature-description">{t(`features.items.${key}.description`)}</p>
                <ul className="feature-list">
                  {(t(`features.items.${key}.details`, { returnObjects: true }) as string[]).map((detail, i) => (
                    <li key={i}><FaCheck /> {detail}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="tech-box">
            <h3><FaBolt /> {t('features.tech.title')}</h3>
            <p className="tech-description">{t('features.tech.description')}</p>
            <div className="tech-stack">
              {['Laravel', 'TypeScript', 'Node.js', 'MySQL', 'React.js', 'React Native'].map(tech => (
                <span key={tech} className="tech-tag">{tech}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section id="roadmap" className="section roadmap-section" ref={roadmapRef}>
        <div className="section-container">
          <div className="section-header">
            <span className="section-badge">{t('roadmap.badge')}</span>
            <h2 className="section-title">
              {t('roadmap.title')} <span className="highlight">{t('roadmap.titleHighlight')}</span>
            </h2>
            <p className="section-subtitle">{t('roadmap.subtitle')}</p>
          </div>

          <div className="roadmap-timeline">
            {roadmapKeys.map((key, index) => {
              const status = roadmapStatus[index];
              return (
                <div key={key} className="timeline-item">
                  <div className={`timeline-dot ${status}`}>
                    {status === 'completed' && <FaCheckCircle size={10} />}
                    {status === 'current' && <FaClock size={10} />}
                    {status === 'future' && <FaCircle size={8} />}
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-date">{t(`roadmap.items.${key}.date`)}</div>
                    <h4 className="timeline-title">{t(`roadmap.items.${key}.title`)}</h4>
                    <p className="timeline-description">{t(`roadmap.items.${key}.description`)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INVESTMENT */}
      <section id="investment" className="section investment-section" ref={investmentRef}>
        <div className="section-container">
          <div className="section-header">
            <span className="section-badge">{t('investment.badge')}</span>
            <h2 className="section-title">
              {t('investment.title')} <span className="highlight">{t('investment.titleHighlight')}</span>
            </h2>
            <p className="section-subtitle">{t('investment.subtitle')}</p>
          </div>

          <div className="investment-grid">
            {(['strategic', 'shortTerm', 'sponsorship'] as const).map((key, index) => {
              const icons = [<FaHandshake key="h" />, <FaChartPie key="c" />, <FaBuilding key="b" />];
              return (
                <div key={key} className={`investment-card ${index === 0 ? 'featured' : ''}`}>
                  <div className="investment-icon">{icons[index]}</div>
                  <h3 className="investment-title">{t(`investment.${key}.title`)}</h3>
                  <ul className="investment-benefits">
                    {(t(`investment.${key}.benefits`, { returnObjects: true }) as string[]).map((benefit, i) => (
                      <li key={i}><FaCheck /> {benefit}</li>
                    ))}
                  </ul>
                  <button
                    className={`btn ${index === 0 ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => scrollToSection('contact')}
                    style={{ width: '100%' }}
                  >
                    {t(`investment.${key}.cta`)} <FaArrowRight style={{ marginLeft: '8px' }} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="section contact-section" ref={contactRef}>
        <div className="section-container">
          <div className="section-header">
            <span className="section-badge">{t('contact.badge')}</span>
            <h2 className="section-title">
              {t('contact.title')}<span className="highlight">{t('contact.titleHighlight')}</span>
            </h2>
            <p className="section-subtitle">{t('contact.subtitle')}</p>
          </div>

          <div className="contact-grid">
            <div className="contact-info-card">
              <h3 className="contact-info-title">{t('contact.infoTitle')}</h3>

              <div className="contact-methods">
                <div className="contact-method">
                  <div className="contact-method-icon whatsapp"><FaWhatsapp /></div>
                  <div className="contact-method-content">
                    <h4>{t('contact.whatsapp')}</h4>
                    {contactInfo.whatsapp.map((number, index) => (
                      <a key={index} href={`https://wa.me/${number.replace('+', '')}?text=${encodeURIComponent(t('contact.whatsappMessage'))}`} target="_blank" rel="noopener noreferrer">
                        {number}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="contact-method">
                  <div className="contact-method-icon phone"><FaPhone /></div>
                  <div className="contact-method-content">
                    <h4>{t('contact.phones')}</h4>
                    {contactInfo.phoneNumbers.map((phone, index) => (
                      <a key={index} href={`tel:${phone}`}>{phone}</a>
                    ))}
                  </div>
                </div>

                <div className="contact-method">
                  <div className="contact-method-icon email"><FaEnvelope /></div>
                  <div className="contact-method-content">
                    <h4>{t('contact.emails')}</h4>
                    {contactInfo.emails.map((email, index) => (
                      <a key={index} href={`mailto:${email}`}>{email}</a>
                    ))}
                  </div>
                </div>

                <div className="contact-method">
                  <div className="contact-method-icon globe"><FaGlobe /></div>
                  <div className="contact-method-content">
                    <h4>{t('contact.website')}</h4>
                    <a href="https://quevvy.com" target="_blank" rel="noopener noreferrer">{t('contact.links.home')}</a>
                    <a href="https://quevvy.com/register" target="_blank" rel="noopener noreferrer">{t('contact.links.register')}</a>
                    <a href="https://quevvy.com/about" target="_blank" rel="noopener noreferrer">{t('contact.links.about')}</a>
                  </div>
                </div>
              </div>

              <div className="contact-social">
                <h4 className="contact-social-title">{t('contact.social')}</h4>
                <div className="social-links">
                  <a href="https://linkedin.com/company/quevvy" target="_blank" rel="noopener noreferrer" className="social-link"><FaLinkedin /></a>
                  <a href="https://www.facebook.com/quevvy.platform" target="_blank" rel="noopener noreferrer" className="social-link"><FaFacebook /></a>
                  <a href="https://chat.whatsapp.com/HXge11ByhzC4yrbhlExW6D" target="_blank" rel="noopener noreferrer" className="social-link"><FaWhatsapp /></a>
                  <a href="https://x.com/quevvyplatform" target="_blank" rel="noopener noreferrer" className="social-link"><FaTwitter /></a>
                  <a href="https://pin.it/1t2XCbLLK" target="_blank" rel="noopener noreferrer" className="social-link"><FaPinterest /></a>
                </div>
              </div>
            </div>

            <div className="contact-form-card">
              <h3 className="contact-form-title">{t('contact.formTitle')}</h3>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">{t('contact.form.name')} *</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required placeholder={t('contact.form.namePlaceholder')} />
                </div>
                <div className="form-group">
                  <label htmlFor="email">{t('contact.form.email')} *</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder={t('contact.form.emailPlaceholder')} />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">{t('contact.form.phone')}</label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} placeholder={t('contact.form.phonePlaceholder')} />
                </div>
                <div className="form-group">
                  <label htmlFor="interest">{t('contact.form.interest')} *</label>
                  <select id="interest" name="interest" value={formData.interest} onChange={handleInputChange} required>
                    {(['investor', 'partner', 'buyer', 'client', 'other'] as const).map(key => (
                      <option key={key} value={key}>{t(`contact.interests.${key}`)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="message">{t('contact.form.message')} *</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} required placeholder={t('contact.form.messagePlaceholder')} rows={5} />
                </div>
                <button type="submit" className="btn btn-primary form-submit">
                  <FaPaperPlane style={{ marginRight: '8px' }} />
                  {t('contact.form.submit')}
                </button>
                <p className="form-note">{t('contact.form.note')}</p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="quevvy-footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/img/quevvy_logo_transparent.png" height="40px" alt="Quevvy" />
              </div>
              <p className="footer-description">{t('footer.description')}</p>
            </div>

            <div>
              <h4 className="footer-title">{t('footer.quickLinks')}</h4>
              <ul className="footer-links">
                <li><a href="https://quevvy.com" target="_blank" rel="noopener noreferrer"><FaExternalLinkAlt /> {t('footer.create')}</a></li>
                <li><a href="https://quevvy.com/about" target="_blank" rel="noopener noreferrer"><FaExternalLinkAlt /> {t('footer.about')}</a></li>
                <li><a href="https://quevvy.com/support" target="_blank" rel="noopener noreferrer"><FaExternalLinkAlt /> {t('footer.support')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="footer-title">{t('footer.legal')}</h4>
              <ul className="footer-links">
                <li><a href="https://quevvy.com/privacy" target="_blank" rel="noopener noreferrer"><FaExternalLinkAlt /> {t('footer.privacy')}</a></li>
                <li><a href="https://quevvy.com/legal" target="_blank" rel="noopener noreferrer"><FaExternalLinkAlt /> {t('footer.legalNotice')}</a></li>
                <li><a href="https://quevvy.com/terms" target="_blank" rel="noopener noreferrer"><FaExternalLinkAlt /> {t('footer.terms')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="footer-title">{t('footer.contactUs')}</h4>
              <div className="footer-contact-item">
                <FaEnvelope />
                <a href="mailto:info@quevvy.com">info@quevvy.com</a>
              </div>
              <div className="footer-contact-item">
                <FaWhatsapp />
                <a href="https://wa.me/243978089552" target="_blank" rel="noopener noreferrer">+243 978 089 552</a>
              </div>
              <a href="https://quevvy.com" target="_blank" rel="noopener noreferrer" className="footer-cta">
                <FaGlobe /> {t('footer.visitSite')}
              </a>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2026 <strong>Quevvy</strong>. {t('footer.copyright')}
            </p>
            <p className="footer-powered">
              {t('footer.powered')} <a href="https://quevvy.com">Quevvy <FaCrown style={{ fontSize: '0.8em' }} /></a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
